/**
 * Toasts — a tiny provider + hook. Errors are announced assertively, the rest
 * politely. Each toast auto-dismisses (errors stay a little longer).
 */
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { AlertTriangle, CheckCircle2, Info, X } from 'lucide-react';

export type ToastKind = 'success' | 'error' | 'info';

export interface ToastInput {
  kind?: ToastKind;
  title: string;
  message?: string;
  /** ms; defaults to 4500 (errors 7000). */
  duration?: number;
}

interface ToastItem extends Required<Pick<ToastInput, 'kind' | 'title'>> {
  id: number;
  message?: string;
}

interface ToastContextValue {
  push: (toast: ToastInput) => void;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

const STYLE: Record<ToastKind, { icon: typeof Info; ring: string; iconColor: string }> = {
  success: { icon: CheckCircle2, ring: 'border-success/30', iconColor: 'text-success' },
  error: { icon: AlertTriangle, ring: 'border-danger/30', iconColor: 'text-danger' },
  info: { icon: Info, ring: 'border-info-blue/30', iconColor: 'text-info-blue' },
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setItems((list) => list.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (input: ToastInput) => {
      const id = nextId.current++;
      const kind = input.kind ?? 'info';
      setItems((list) => [...list.slice(-3), { id, kind, title: input.title, message: input.message }]);
      const ms = input.duration ?? (kind === 'error' ? 7000 : 4500);
      setTimeout(() => dismiss(id), ms);
    },
    [dismiss],
  );

  const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        className="pointer-events-none fixed inset-x-3 bottom-3 z-[80] flex flex-col items-center gap-2 sm:inset-x-auto sm:right-4 sm:bottom-4 sm:items-end"
        aria-live="polite"
      >
        {items.map((t) => {
          const s = STYLE[t.kind];
          const Icon = s.icon;
          return (
            <div
              key={t.id}
              role={t.kind === 'error' ? 'alert' : 'status'}
              className={`pointer-events-auto flex w-full max-w-sm items-start gap-2.5 rounded-lg border bg-white px-3 py-2.5 text-sm shadow-lg ${s.ring}`}
            >
              <Icon size={16} strokeWidth={2} className={`mt-0.5 shrink-0 ${s.iconColor}`} aria-hidden />
              <div className="min-w-0 flex-1">
                <div className="font-medium text-gray-900">{t.title}</div>
                {t.message && <div className="mt-0.5 text-[13px] text-muted">{t.message}</div>}
              </div>
              <button
                type="button"
                onClick={() => dismiss(t.id)}
                aria-label="Dismiss notification"
                className="-mr-2 -mt-1.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-800"
              >
                <X size={14} strokeWidth={2} aria-hidden />
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}

/** Human message for a thrown value — ApiError carries `message`; anything else gets a fallback. */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Try again.'): string {
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
